import { LitElement, css, html, svg } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { VerificationState } from './verification-factors.js';

export type { VerificationState };

/** Same mark geometry as the verification/{verified,warning,invalid}.svg icon set (see
 * ns-verification-badge.ts), redrawn without the colored circle backdrop so it can sit inside this
 * component's own white badge circle instead. */
const STATE_MARKS: Record<VerificationState, ReturnType<typeof svg>> = {
  verified: svg`<path d="M5 12.5L9.5 17L19 6" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />`,
  warning: svg`<line x1="5" y1="12" x2="10" y2="12" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
    <line x1="14" y1="12" x2="19" y2="12" stroke="currentColor" stroke-width="3" stroke-linecap="round" />`,
  invalid: svg`<path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="3" stroke-linecap="round" />`,
};

/** A colored pill labeling a service/provider (e.g. "Companies House"), with a small white badge
 * overlapping its trailing edge carrying a check/dash/X mark for its VerificationState. Generic
 * and presentational (no data-derivation logic) so it's reusable anywhere a named service's status
 * needs to be shown - not just the verification popup. */
@customElement('ns-service-status-pill')
export class NSServiceStatusPill extends LitElement {
  @property({ type: String })
  label = '';

  @property({ type: String })
  state: VerificationState = 'invalid';

  static styles = [
    css`
      :host {
        display: inline-flex;
      }

      .pill {
        position: relative;
        display: inline-flex;
        align-items: center;
        box-sizing: border-box;

        height: 30px;
        padding: 0 var(--wa-space-xl) 0 var(--wa-space-s);
        border-radius: var(--wa-border-radius-pill);

        color: var(--ns-theme-white);
        font-size: var(--wa-font-size-s);
        font-weight: 500;
        line-height: 1;
        white-space: nowrap;
        letter-spacing: 1px;
      }

      .pill--verified {
        background: var(--ns-theme-green);
        color: var(--ns-theme-type);
      }

      .pill--warning {
        background: var(--ns-theme-amber);
        color: var(--ns-theme-type);
      }

      .pill--invalid {
        background: var(--ns-theme-red);
        color: var(--ns-theme-black);
      }

      .badge {
        position: absolute;
        right: 5px;
        top: 50%;
        transform: translateY(-50%);
        box-sizing: border-box;

        display: flex;
        align-items: center;
        justify-content: center;

        width: 22px;
        height: 22px;
        border-radius: var(--wa-border-radius-circle);

        background: var(--ns-theme-container);
        color: var(--ns-theme-type);

        box-shadow: 0 1px 3px color-mix(in srgb, var(--ns-theme-black) 30%, transparent);
      }

      .badge svg {
        width: 12px;
        height: 12px;
      }
    `,
  ];

  render() {
    return html`
      <div class="pill pill--${this.state}">
        <span class="label">${this.label}</span>
        <span class="badge">
          <svg viewBox="0 0 24 24" role="img" aria-label="${this.state}">${STATE_MARKS[this.state]}</svg>
        </span>
      </div>
    `;
  }
}
